/**
 * Utility functions for handling dates to prevent timezone issues
 */

/**
 * Normalizes a date to UTC midnight (noon) to prevent timezone shift issues
 * when sending dates to the backend. This ensures that the date selected by the user
 * is preserved regardless of their timezone.
 * 
 * @param date The date to normalize
 * @returns A new Date object set to noon UTC of the same calendar date
 */
export function normalizeDateToUTC(date: Date): Date {
    return new Date(Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        12, 0, 0, 0  // Set to noon UTC to avoid DST issues
    ));
}

/**
 * Normalizes a date to local midnight to ensure consistent date handling
 * when working with date-only values.
 *
 * @param date The date to normalize
 * @returns A new Date object set to midnight local time of the same calendar date
 */
export function normalizeDateToLocalMidnight(date: Date): Date {
    return new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        0, 0, 0, 0
    );
}

/**
 * Converts a local date to a UTC ISO string for sending to the backend.
 * This preserves the calendar date regardless of the user's timezone.
 *
 * @param date The date to convert (can be null/undefined)
 * @returns ISO string at UTC midnight, or undefined if date is null/undefined
 *
 * @example
 * // User in CET (UTC+1) selects Dec 18, 2025
 * toUtcDateString(new Date(2025, 11, 18)) // "2025-12-18T00:00:00.000Z"
 */
export function toUtcDateString(date: Date | null | undefined): string | undefined {
    if (!date) return undefined;
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString();
}

/**
 * Normalizes a date to end of day UTC (23:59:59) to represent the last moment
 * of a calendar date. Used for cancellation/withdrawal dates where the entity
 * should remain valid until the end of the selected date.
 *
 * @param date The date to normalize
 * @returns A new Date object set to 23:59:59 UTC of the same calendar date
 *
 * @example
 * // User selects Dec 31, 2025
 * normalizeDateToEndOfDayUTC(new Date(2025, 11, 31)) // 2025-12-31T23:59:59.000Z
 */
export function normalizeDateToEndOfDayUTC(date: Date): Date {
    return new Date(Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        23, 59, 59, 0
    ));
}

/**
 * Gets the last day of the previous year as a local date.
 * Returns a local Date object so date pickers display the correct date.
 * Useful for quick-select buttons in cancellation/withdrawal dialogs.
 *
 * Note: Use normalizeDateToEndOfDayUTC() when sending to backend.
 *
 * @returns A new Date object set to Dec 31 of the previous year (local time)
 */
export function getEndOfLastYear(): Date {
    const lastYear = new Date().getFullYear() - 1;
    return new Date(lastYear, 11, 31);
}
